/**
 * Creator scope: which creators a conversation is asking.
 *
 * The user names creators by typing `@handle` in the composer. Those handles
 * resolve to the corpus ids the agent filters retrieval by (`creator_ids`),
 * and the resolved ids stick to the conversation until removed, so a
 * follow-up question stays with the creator it was asked of.
 *
 * Handles are what people type and read; corpus ids are what the agent
 * indexes under (`carolynscott` -> `carolynscott_platform`). One handle can
 * own several ids, so scope is always a list of ids and the UI groups them
 * back into one chip per handle.
 */

import { normalizeCreatorHandle, toCorpusCreatorId } from "./creatorMentions";
import { cleanText } from "./textSanitizer";

const MENTION_PATTERN = /(^|[^A-Za-z0-9_@./-])@([A-Za-z0-9][A-Za-z0-9_.]*)/g;

/** Handles named in a message, in the order typed, without duplicates. */
export function extractMentionHandles(text) {
  const source = String(text || "");
  const handles = [];
  const seen = new Set();

  for (const match of source.matchAll(MENTION_PATTERN)) {
    const handle = normalizeCreatorHandle(match[2]);
    if (!handle) continue;
    const key = handle.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    handles.push(handle);
  }

  return handles;
}

/**
 * The message without its `@mentions`. Once a handle is a chip it is said
 * twice, so the composer keeps the chip and drops the word.
 */
export function stripMentions(text) {
  // Trailing space is left alone: the composer strips as the user types, and
  // the space after a name is usually the start of the next word.
  return String(text || "")
    .replace(MENTION_PATTERN, (match, lead) => lead)
    .replace(/[ \t]{2,}/g, " ")
    .replace(/^[ \t]+/gm, "");
}

/** Corpus ids the conversation has already seen, learned from content links. */
export function creatorIdsInMessages(messages = []) {
  const ids = [];
  const seen = new Set();

  for (const message of messages) {
    const links = message?.data?.agent?.contentLinks;
    if (!Array.isArray(links)) continue;

    for (const link of links) {
      const id = cleanText(link?.creator_id);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      ids.push(id);
    }
  }

  return ids;
}

/**
 * Corpus ids for one handle. The agent indexes people as
 * `{handle}_platform` (or another platform suffix already on the id).
 * Bare handles are never sent on their own.
 */
export function creatorIdsForHandle(handle, knownIds = []) {
  const wanted = normalizeCreatorHandle(handle);
  if (!wanted) return [];

  const matches = knownIds.filter(
    (id) => normalizeCreatorHandle(id).toLowerCase() === wanted.toLowerCase(),
  );

  if (matches.length > 0) {
    return ensureCorpusCreatorIds(matches);
  }

  return ensureCorpusCreatorIds([wanted]);
}

/** Every id in its corpus form, `_platform` added when it was missing. */
export function ensureCorpusCreatorIds(ids = []) {
  return mergeCreatorIds(
    (Array.isArray(ids) ? ids : []).map(toCorpusCreatorId),
  );
}

/** Corpus ids for every `@handle` in a message. */
export function resolveMentionedCreatorIds(text, knownIds = []) {
  return mergeCreatorIds(
    ...extractMentionHandles(text).map((handle) =>
      creatorIdsForHandle(handle, knownIds),
    ),
  );
}

export function mergeCreatorIds(...lists) {
  const merged = [];
  const seen = new Set();

  for (const list of lists) {
    for (const value of Array.isArray(list) ? list : []) {
      const id = cleanText(value);
      if (!id || seen.has(id)) continue;
      seen.add(id);
      merged.push(id);
    }
  }

  return merged;
}

/** Every id sharing a handle drops together — the chip names the creator. */
export function removeCreatorHandleFromScope(creatorIds = [], handle) {
  const unwanted = normalizeCreatorHandle(handle).toLowerCase();
  if (!unwanted) return [...creatorIds];

  return creatorIds.filter(
    (id) => normalizeCreatorHandle(id).toLowerCase() !== unwanted,
  );
}

/** Scope as the UI shows it: one entry per handle, holding its ids. */
export function groupCreatorIdsByHandle(creatorIds = []) {
  const groups = new Map();

  for (const value of creatorIds) {
    const id = cleanText(value);
    if (!id) continue;

    const handle = normalizeCreatorHandle(id) || id;
    const key = handle.toLowerCase();
    const group = groups.get(key);
    if (group) {
      group.ids.push(id);
    } else {
      groups.set(key, { handle, ids: [id] });
    }
  }

  return [...groups.values()];
}
